// The DOM overlay for a match.
//
// Card text, scores and prompts live in the DOM rather than in the 3D scene so
// they stay crisp at any resolution, remain selectable, and are readable by a
// screen reader. The canvas draws the table; this draws everything you need to
// read.

import type { CardDef, Domain, GameEvent, GameState, PlayerId } from '../rules/types.ts';
import { DECLINE, DOMAINS } from '../rules/types.ts';
import type { Match } from './match.ts';
import type { Board, OverlayItem } from './board.ts';
import { SYMBOLS } from './symbols.ts';
import { buildStamp, installed } from '../pwa.ts';
import { domainRune, symbolParts, typeHtml } from './symbols.ts';
import { drawnTo, headPoints, scaledQuad } from './aim.ts';


import { cardTextLines } from './cardtext.ts';
import { victoryTarget } from '../rules/state.ts';
import { load as loadSettings, save as saveSettings, type Settings } from './settings.ts';
import { audio } from './audio.ts';

/**
 * 137/414/441. A colour per status, so a glance at a unit says what is on it.
 *
 * Only the ones that change how a unit behaves get a mark; a status nobody can
 * act on is noise on a card that is already busy.
 */
const MARK_COLOR: Record<string, string> = {
  exhausted: '#8c93a8',
  stunned: '#c46bd8',
  buffed: '#6fe08a',
  empowered: '#f0b13c',
  attacking: '#e2604f',
  defending: '#3f9bd6',
  shielded: '#63c9d8',
};

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

/**
 * A compiled mode label is normalised card text; this makes it printable again.
 *
 * The compiler lowercases everything it reads, symbols included, so `[M]`
 * arrives as `[m]` and would miss the icon table. Putting the brackets back in
 * upper case is safe -- every symbol in that alphabet is a distinct capital,
 * so nothing is ambiguous once it is one -- and the leading capital is what
 * makes the line read as a sentence rather than a fragment.
 */
const modeLabel = (text: string): string =>
  (text.charAt(0).toUpperCase() + text.slice(1))
    .replace(/\[([a-z])\]/g, (_, c: string) => `[${c.toUpperCase()}]`);

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const PHASE_LABEL: Record<string, string> = {
  awaken: 'Awaken', beginning: 'Beginning', channel: 'Channel',
  draw: 'Draw', main: 'Main', ending: 'Ending',
};

/**
 * 135.2.e. The Power symbol for each domain, in the compiler's shorthand.
 *
 * Not the domain's own letter: the font's table is riftcardmaker's, where "R"
 * is the rainbow rune and Fury is "f" -- guessing from the initial gets two of
 * them wrong.
 */
const POWER_SYMBOL: Record<Domain, string> = {
  fury: 'R', calm: 'G', mind: 'B', body: 'O', chaos: 'P', order: 'Y',
};

// The same six colours the icons use. They disagreed: Calm was teal here and
// green there, Body green here and orange there, so an orange card wore a green
// pip. The art settles it -- see domains.test.ts.
const DOMAIN_HEX: Record<Domain, string> = {
  fury: '#e0574a', calm: '#56b96a', mind: '#4d92d8',
  body: '#e2913f', chaos: '#a06fd0', order: '#e0c04e',
};



/**
 * 801. The keyword palette, taken from riftcardmaker -- the same tool the icon
 * font comes from -- so a keyword reads the same colour here as on the printed
 * card. Its own format spells one out: `{Deflect#94B131#000000}`.
 *
 * The groups are the game's own: teal for the ones about *when* a card may be
 * played, lime for standing and triggered abilities, grey for the ones that
 * name an action another card takes, and pink for Backline, which is alone in
 * being about where a unit stands.
 */
/**
 * Where "play again" goes.
 *
 * It was a literal `?` in the markup, which drops the whole query string --
 * and the query string is load-bearing twice over. Outside Discord,
 * `show=true` is what lifts the holding page, so playing again landed on "down
 * for maintenance" with no way back but typing the URL again. Inside Discord,
 * `frame_id` is the documented signal that this page *is* an Activity, so the
 * reloaded page stopped being one and the Activity had to be restarted.
 *
 * Everything is kept except the flags that name one particular match: a new
 * game is the whole point, so the seed goes, and a replay id would reopen the
 * recording instead of starting anything.
 */
export function playAgainHref(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('seed');
  params.delete('replay');
  const query = params.toString();
  return query ? `?${query}` : '?';
}

/**
 * How much of a wheel notch reaches the card panel.
 *
 * A notch is about 100 pixels, and this panel is a few hundred tall -- so
 * passing the delta through sent it most of the way down the card at a flick.
 * A third of that is about three lines, which is what a wheel over a small
 * panel should be worth.
 */
const DETAIL_SCROLL_STEP = 0.34;

/**
 * The outline captions worth printing.
 *
 * Every drop target carries a label, and almost all of them say what the
 * player already knows -- a highlighted battlefield is obviously somewhere to
 * go. These two are the exception: they are the whole of how a group move
 * announces itself.
 */
const MOVE_LABELS = new Set(['Going', '+ Send too']);

const KEYWORD_TEAL = '#2fbcae';
const KEYWORD_LIME = '#94b131';
const KEYWORD_GREY = '#98a2ac';
const KEYWORD_PINK = '#e8447f';

const KEYWORD_COLOR: Record<string, string> = {
  accelerate: KEYWORD_TEAL, action: KEYWORD_TEAL, legion: KEYWORD_TEAL,
  reaction: KEYWORD_TEAL, repeat: KEYWORD_TEAL, hidden: KEYWORD_TEAL,
  'quick-draw': KEYWORD_TEAL, quickdraw: KEYWORD_TEAL, ambush: KEYWORD_TEAL,
  flow: KEYWORD_TEAL, assault: KEYWORD_TEAL, shield: KEYWORD_TEAL, tank: KEYWORD_TEAL,

  ganking: KEYWORD_LIME, deflect: KEYWORD_LIME, deathknell: KEYWORD_LIME,
  temporary: KEYWORD_LIME, hunt: KEYWORD_LIME, level: KEYWORD_LIME,
  empowered: KEYWORD_LIME,

  add: KEYWORD_GREY, buff: KEYWORD_GREY, stun: KEYWORD_GREY, equip: KEYWORD_GREY,
  mighty: KEYWORD_GREY, vision: KEYWORD_GREY, weaponmaster: KEYWORD_GREY,
  unique: KEYWORD_GREY, predict: KEYWORD_GREY, empower: KEYWORD_GREY,
  burn: KEYWORD_GREY,

  backline: KEYWORD_PINK,
};

/** A named player in a seat: a Discord user, or whatever else names one later. */
export interface SeatIdentity {
  name: string;
  avatarUrl: string | null;
}

export class Hud {
  private readonly session: Match;
  private readonly board: Board;

  private readonly root = el('hud');
  private readonly prompt = el('hud-prompt');
  private readonly turn = el('hud-turn');
  private readonly plateMe = el('hud-me');
  private readonly plateThem = el('hud-them');
  private readonly pool = el('hud-pool');
  private readonly detail = el('hud-detail');
  /** Where the card panel is easing to, and the frame callback doing it. */
  private detailScroll = { target: 0, raf: 0 };
  private readonly endTurn = el<HTMLButtonElement>('hud-end');
  private readonly over = el('hud-over');
  private readonly overText = el('hud-over-text');
  private readonly overlay = el('hud-overlay');
  private readonly drops = el<HTMLElement>('hud-drops');
  private readonly pile = el('hud-pile');
  private readonly pileText = el('hud-pile-text');
  private readonly pileList = el('hud-pile-list');
  private readonly pileClose = el<HTMLButtonElement>('hud-pile-close');
  /** Captions on the outlines a group move puts up, by overlay key. */
  private readonly dropTags = new Map<string, HTMLElement>();
  /** Badge nodes by object id, reused across frames so the DOM is not rebuilt. */
  private readonly badges = new Map<number, HTMLElement>();
  /** Pile and XP counts, keyed by what they count rather than by a card. */
  private readonly counts = new Map<string, HTMLElement>();
  private readonly choice = el('hud-choice');
  private readonly choiceText = el('hud-choice-text');
  private readonly choiceList = el('hud-choice-list');
  private readonly logPanel = el('hud-log');
  private readonly logList = el('hud-log-list');
  private readonly settingsPanel = el('hud-settings');
  /** How many log entries are already drawn, so only new ones are built. */
  private logDrawn = 0;
  private settings: Settings = loadSettings();

  private lastCard: string | null = null;
  /** Clears the toast; reset on every new message so a burst reads as one. */
  private promptTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Who is sitting in each seat, when anything knows. Empty in a plain browser
   * game, where "You" and "Opponent" are the honest labels.
   */
  private seats: { me?: SeatIdentity; them?: SeatIdentity } = {};
  private readonly detach: Array<() => void> = [];

  constructor(session: Match, board: Board) {
    this.session = session;
    this.board = board;
    this.root.hidden = false;

    const click = (node: HTMLElement, fn: () => void) => {
      node.addEventListener('click', fn);
      this.detach.push(() => node.removeEventListener('click', fn));
    };
    click(this.endTurn, () => this.onEndTurn());
    click(this.pileClose, () => this.board.closeInspect());
    click(el('hud-rewind'), () => this.onRewind());
    click(el('hud-log-close'), () => this.showLog(false));
    click(el('hud-log-save'), () => this.saveReplay());
    // Computed rather than written in the markup, because the right answer
    // depends on the flags this page was opened with.
    el<HTMLAnchorElement>('hud-again').href = playAgainHref(
      typeof location === 'object' ? location.search : '',
    );
    click(el('hud-gear'), () => this.showSettings(true));
    click(el('hud-settings-close'), () => this.showSettings(false));
    // Which build is running, and whether it is the installed app or a tab --
    // the two update differently, so knowing which one you are looking at is
    // half the answer when a change has not appeared.
    el('set-buildstamp').textContent = `${buildStamp()}${installed() ? ' · installed' : ''}`;

    // "+" opens the match log. Chosen because nothing else in the client uses
    // it, and it is reachable without a modifier on most layouts.
    const key = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // Ctrl+Z is what everyone already tries.
      if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.onRewind();
        return;
      }
      if (e.key === '+' || (e.key === '=' && e.shiftKey)) {
        e.preventDefault();
        this.showLog(this.logPanel.hidden);
      }
    };
    window.addEventListener('keydown', key);
    this.detach.push(() => window.removeEventListener('keydown', key));

    // Loaded here rather than at boot: a browser will not play anything before
    // a gesture anyway, and the HUD is built at the moment the match starts.
    audio.load();
    this.bindSettings();
    this.applySettings();
    this.bindSounds();
    if (this.settings.showLog) this.showLog(true);

    // 355.3 / 424. One listener on the list rather than one per card: the list
    // is rebuilt every time the question changes, and per-card listeners would
    // have to be torn down with it.
    const answer = (e: Event) => {
      const button = (e.target as HTMLElement | null)?.closest?.('.choice');
      if (!(button instanceof HTMLElement)) return;
      // The same list serves the engine's questions and the board's own menu.
      if (button.dataset.menu !== undefined) {
        const item = Number(button.dataset.menu);
        if (Number.isFinite(item)) this.board.pickMenu(item);
        return;
      }
      const pick = Number(button.dataset.pick);
      if (!Number.isFinite(pick)) return;
      this.board.answerChoice(pick);
    };
    this.choiceList.addEventListener('click', answer);
    this.detach.push(() => this.choiceList.removeEventListener('click', answer));

    this.update();
  }

  private onEndTurn(): void {
    // The same button does the sensible thing in every state. What that is
    // comes from the engine's own list of legal actions rather than from the
    // shape of the board -- combat can be in progress while the only legal
    // action is assigning damage, and pressing "Pass" there does nothing.
    const action = this.board.primaryAction();
    if (action) this.board.submitPrimary(action);
  }

  /**
   * Says something, briefly.
   *
   * A toast rather than a line of text in the corner: these are announcements
   * about the click you just made ("that rune is exhausted"), and one that
   * stays up is still on screen three actions later, describing nothing. The
   * timer is reset on each message so a burst reads as one notice.
   */
  setPrompt(text: string): void {
    this.prompt.textContent = text;
    this.prompt.classList.toggle('showing', text.length > 0);
    if (this.promptTimer !== null) clearTimeout(this.promptTimer);
    this.promptTimer = null;
    if (text.length === 0) return;
    this.promptTimer = setTimeout(() => {
      this.prompt.classList.remove('showing');
      this.promptTimer = null;
    }, 3600);
  }

  /**
   * Draws the table overlay: a unit's live numbers, and the outline of
   * anywhere a pending choice will accept a click.
   *
   * Called every frame, so nodes are reused and only their transforms and
   * changed text are touched -- rebuilding twenty badges sixty times a second
   * would show up as jank long before it showed up as a profile.
   */
  setOverlay(items: OverlayItem[]): void {
    const rect = this.overlay.getBoundingClientRect();
    const seen = new Set<number>();
    const seenCounts = new Set<string>();
    const seenTags = new Set<string>();
    const shapes: string[] = [];

    for (const item of items) {
      // 327. The chain, made visible.
      //
      // The line *draws itself* from the caster to the target rather than
      // arriving whole: an arrow that appears fully formed reads as a label
      // being switched on, and one that travels reads as the spell reaching
      // for something. The dashes keep moving along it afterwards, so a chain
      // that is waiting looks like it is waiting rather than like a decal.
      if (item.kind === 'aim') {
        const ease = item.appear * item.appear * (3 - 2 * item.appear);
        // Only the part of the curve that has been reached so far. The points
        // are already flattened, so this is a slice and a lerp on the last
        // segment rather than re-solving the curve.
        const drawn = drawnTo(item.shaft, ease);
        if (drawn.length < 2) continue;
        const pts = drawn.map(([x, y]) => `${x},${y}`).join(' ');
        const ink = item.mine ? 'rgba(240,205,120,0.95)' : 'rgba(232,110,96,0.95)';
        const halo = 'rgba(0,0,0,0.5)';
        // Travels toward the target. Negative offset because a dash pattern
        // scrolls against the offset, and the shaft runs source-to-target.
        const flow = (-item.time * 26) % 12;
        // The head only lands once the line has arrived, and grows into place.
        const settle = Math.max(0, (ease - 0.72) / 0.28);
        const head = settle > 0.01
          ? `<polygon points="${headPoints(item.head, settle)}" fill="${ink}"`
            + ` stroke="${halo}" stroke-width="1.5" stroke-linejoin="round"`
            + ' stroke-linejoin="round" vector-effect="non-scaling-stroke" />'
          : '';
        shapes.push(
          `<polyline points="${pts}" fill="none" stroke="${halo}" stroke-width="6"`
          + ` opacity="${(ease * 0.9).toFixed(3)}"`
          + ' stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />'
          + `<polyline points="${pts}" fill="none" stroke="${ink}" stroke-width="2.5"`
          + ` opacity="${ease.toFixed(3)}" stroke-dasharray="7 5"`
          + ` stroke-dashoffset="${flow.toFixed(2)}"`
          + ' stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />'
          + head,
        );
        continue;
      }

      // 164.2. A rune this play is about to spend. Gold for a tap -- it comes
      // back -- and red for a recycle, which does not: that rune goes to the
      // Rune Deck for the rest of the game, and the only warning used to be
      // noticing it missing afterwards.
      if (item.kind === 'cost') {
        const ease = item.appear * item.appear * (3 - 2 * item.appear);
        const pts = item.points.map(([x, y]) => `${x},${y}`).join(' ');
        const ink = item.destructive ? '226,112,90' : '240,205,120';
        shapes.push(
          `<polygon points="${pts}" fill="rgba(${ink},${(0.10 * ease).toFixed(3)})"`
          + ` stroke="rgba(${ink},${(0.95 * ease).toFixed(3)})" stroke-width="2.5"`
          + (item.destructive ? ' stroke-dasharray="7 5"' : '')
          + ' stroke-linejoin="round" vector-effect="non-scaling-stroke" />',
        );
        continue;
      }

      if (item.kind === 'drop') {
        // Settles into place rather than switching on: eased opacity, and the
        // quad grows the last few per cent into its own footprint. `hot` is a
        // ramp too, so moving the pointer between two targets is a crossfade
        // and not two hard cuts.
        const ease = item.appear * item.appear * (3 - 2 * item.appear);
        const pts = scaledQuad(item.points, 0.94 + 0.06 * ease)
          .map(([x, y]) => `${x},${y}`).join(' ');
        const fill = 0.05 + 0.09 * item.hot;
        const line = 0.55 + 0.40 * item.hot;
        shapes.push(
          `<polygon points="${pts}" fill="rgba(216,180,90,${(fill * ease).toFixed(3)})"`
          + ` stroke="rgba(${(216 + 24 * item.hot).toFixed(0)},${(180 + 25 * item.hot).toFixed(0)},`
          + `${(90 + 30 * item.hot).toFixed(0)},${(line * ease).toFixed(3)})"`
          + ` stroke-width="${(1.5 + 1.5 * item.hot).toFixed(2)}"`
          + ' vector-effect="non-scaling-stroke" />',
        );

        // 144.3. An outline that means something other than "aim here" says
        // so, and only the two that name a group move do -- a board where
        // every highlighted card carried a caption would be unreadable, and
        // the reason this exists at all is that a player could not tell that
        // sending a second unit along was possible.
        if (MOVE_LABELS.has(item.label)) {
          seenTags.add(item.key);
          let tag = this.dropTags.get(item.key);
          if (!tag) {
            tag = document.createElement('div');
            tag.className = 'droptag';
            this.overlay.append(tag);
            this.dropTags.set(item.key, tag);
          }
          // Under the card rather than over it: the artwork is what the player
          // is choosing between.
          const left = item.points.reduce((n, q) => n + q[0], 0) / item.points.length;
          const bottom = Math.max(...item.points.map((q) => q[1]));
          tag.style.left = `${left * rect.width}px`;
          tag.style.top = `${bottom * rect.height}px`;
          tag.style.opacity = ease.toFixed(3);
          tag.classList.toggle('going', item.label === 'Going');
          if (tag.textContent !== item.label) tag.textContent = item.label;
        }
        continue;
      }

      if (item.kind === 'count') {
        // 108. Kept in its own map: a pile's count is keyed by the zone it
        // stands for, not by the object id of whichever card happens to be on
        // top -- that card changes every time the pile is drawn from.
        seenCounts.add(item.key);
        let tag = this.counts.get(item.key);
        if (!tag) {
          tag = document.createElement('div');
          tag.className = 'pilecount';
          this.overlay.append(tag);
          this.counts.set(item.key, tag);
        }
        const px = Math.max(8, Math.min(15, item.size * rect.height * 0.20));
        tag.style.left = `${item.at[0] * rect.width}px`;
        tag.style.top = `${item.at[1] * rect.height}px`;
        tag.style.fontSize = `${px}px`;
        tag.classList.toggle('low', item.low);
        tag.classList.toggle('theirs', !item.mine);
        tag.classList.toggle('xp', item.label === 'xp');
        // 108. The mat already prints "MAIN DECK" and "RUNE DECK" under these
        // piles, so repeating the word doubles it and turns a quiet count into
        // clutter. Only XP names itself: nothing on the table says what it is.
        const html = item.label === 'xp'
          ? `<b>${item.value}</b><span>xp</span>`
          : `<b>${item.value}</b>`;
        if (tag.innerHTML !== html) tag.innerHTML = html;
        continue;
      }

      seen.add(item.id);
      let node = this.badges.get(item.id);
      if (!node) {
        node = document.createElement('div');
        node.className = 'badge';
        this.overlay.append(node);
        this.badges.set(item.id, node);
      }

      const size = Math.max(9, Math.min(20, item.size * rect.height * 0.30));
      node.style.left = `${item.at[0] * rect.width}px`;
      node.style.top = `${item.at[1] * rect.height}px`;
      node.style.fontSize = `${size}px`;
      node.classList.toggle('theirs', !item.mine);

      const swing = item.might > item.printed ? 'up' : item.might < item.printed ? 'down' : '';
      const marks = item.marks
        .map((m) => MARK_COLOR[m])
        .filter((c): c is string => !!c)
        .map((c) => `<span class="mark" style="background:${c}"></span>`)
        .join('');
      // A card with no printed Might shows none: gear and runes have statuses
      // worth marking but no number, and "0" is a different claim from "none".
      const html = (item.hasMight ? `<span class="might ${swing}">${item.might}</span>` : '')
        + (item.damage > 0 ? `<span class="dmg">-${item.damage}</span>` : '')
        + marks;
      if (node.dataset.html !== html) {
        node.innerHTML = html;
        node.dataset.html = html;
      }
    }

    for (const [id, node] of this.badges) {
      if (seen.has(id)) continue;
      node.remove();
      this.badges.delete(id);
    }
    for (const [key, node] of this.counts) {
      if (seenCounts.has(key)) continue;
      node.remove();
      this.counts.delete(key);
    }
    for (const [key, node] of this.dropTags) {
      if (seenTags.has(key)) continue;
      node.remove();
      this.dropTags.delete(key);
    }
    this.drops.innerHTML = shapes.join('');
  }

  /** 108. Shows a pile the player asked to look through, or closes it. */
  setInspect(view: { title: string; cards: string[] } | null): void {
    if (!view) { this.pile.hidden = true; this.pileList.replaceChildren(); return; }
    this.pileText.textContent = view.title;
    if (view.cards.length === 0) {
      this.pileList.innerHTML = '<div class="choice-sub">Nothing here yet.</div>';
    } else {
      this.pileList.innerHTML = view.cards.map((id) => {
        const def = this.session.rules.index.get(id);
        return `
          <div class="choice">
            ${this.artTag(def.id, 'choice-blank')}
            <span class="choice-name">${escapeHtml(def.name)}</span>
            <span class="choice-sub">${escapeHtml(def.type)}</span>
          </div>`;
      }).join('');
    }
    this.pile.hidden = false;
  }

  /** Shows the full card under the pointer. */
  /**
   * Shows a card in the rail.
   *
   * Moving off a card leaves the last one up rather than clearing the panel:
   * the rail is for reading, and blanking it every time the pointer crosses a
   * gap makes it flicker and lose the text mid-sentence.
   */
  setHovered(cardId: string | null): void {
    if (!cardId || cardId === this.lastCard) return;
    this.lastCard = cardId;

    // A different card starts at the top, and the easing that was carrying the
    // last one must not keep pulling on the new one's body.
    this.resetDetailScroll();
    const def = this.session.rules.index.get(cardId);
    this.detail.hidden = false;
    this.detail.innerHTML = this.renderCard(def);
  }

  /**
   * The artwork for a card, if the pipeline produced one.
   *
   * The path is derived from the id rather than stored: `fetch-art` writes
   * `<id>.webp`, and `available` is the manifest of what it actually wrote.
   */
  private artTag(id: string, cls: string): string {
    if (!this.session.rules.available.has(id)) return `<div class="${cls}"></div>`;
    return `<img class="${cls}" src="/cards/${encodeURIComponent(id)}.webp" alt="" />`;
  }

  /**
   * The card's picture for the detail panel, as a croppable element.
   *
   * A `<div>` with the art as a background rather than an `<img>`, because on
   * a narrow screen the panel shows only the *illustration* -- the printed
   * frame, name and rules box are all repeated as real text right below it, so
   * a shrunken picture of them is the one part of the card worth losing. A
   * background can be windowed in CSS; an `<img>` cannot without a wrapper.
   *
   * The window differs by card: a unit's art sits high and narrow, a rune's
   * lower, and a battlefield is a landscape printing altogether.
   */
  private detailArt(def: CardDef, overlay = ''): string {
    const kind = def.type === 'battlefield' ? 'battlefield'
      : def.type === 'rune' ? 'rune' : 'card';
    if (!this.session.rules.available.has(def.id)) {
      return `<div class="detail-art art-${kind}">${overlay}</div>`;
    }
    const url = `/cards/${encodeURIComponent(def.id)}.webp`;
    return `<div class="detail-art art-${kind}" style="--art:url('${url}')">${overlay}</div>`;
  }

  private renderCard(def: CardDef): string {
    // The head takes the colour of what the card costs. Two domains lean
    // across it as a tilted blend, which is the one place on the panel that
    // says at a glance what a card is made of.
    // The printed motif behind the name, which differs by domain -- Order's
    // angular knots, Fury's spoked wheels, Chaos's fronds.
    //
    // A two-domain card carries *both*, one at each end, and which goes where
    // is theirs: `marks` is drawn from the second domain and `marksStart` from
    // the first, so the first reads on the left and the second on the right.
    // A single-domain card has only the right-hand set.
    const headTexture = (domains: readonly string[]): string => {
      const url = (d: string, side = ''): string =>
        `url('/generated/namebar-${encodeURIComponent(d)}${side}.svg')`;
      const first = domains[0];
      if (!first) return '--head-tex:none;--head-tex-start:none;--head-dots:none';
      const right = domains[1] ?? first;
      return `--head-tex:${url(right)}`
        + `;--head-tex-start:${domains[1] ? url(first, '-start') : 'none'}`
        // The speckled dissolve is the first domain's dark shade, which is the
        // end of the bar it sits on.
        + `;--head-dots:url('/generated/namebar-${encodeURIComponent(first)}-dots.png')`
        // The far end takes the domain that owns it, mirrored so both
        // dissolves fade *inward* from their own edge.
        + `;--head-dots-end:url('/generated/namebar-${
          encodeURIComponent(right)}-dots-end.png')`;
    };

    const headBackground = (domains: readonly string[]): string => {
      const vars = domains.slice(0, 2).map((d) => `var(--bar-${d})`);
      if (vars.length === 0) return 'transparent';
      // One source for the palette: these are the same custom properties the
      // rune glyphs are coloured from, so a head can never be a colour its
      // own cost symbols are not.
      // Held, then handed over. A plain two-stop blend spreads the change
       // across the whole bar and both colours end up washed through the
       // middle; the printed card holds each one and crosses between them
       // quickly, so the join reads as an edge rather than as a fade.
      return vars.length === 1
        ? vars[0]!
        // 135deg, not 45: the edge leans the other way, so the first domain
        // holds the top-left corner and hands over toward the bottom-right.
        : `linear-gradient(135deg, ${vars[0]} 0%, ${vars[0]} 40%,`
          + ` ${vars[1]} 60%, ${vars[1]} 100%)`;
    };

    // The costs read as the card prints them: the energy numeral, the domain
    // pips and the Might blade, in the game's own icon font. "2 energy" is a
    // translation of a symbol the player is already looking at.
    const stats: string[] = [];
    if (def.cost.energy > 0) {
      stats.push(`<span class="stat">${this.symbol(String(def.cost.energy)) ?? def.cost.energy}</span>`);
    }
    if (def.cost.power > 0) {
      // 135.2.e. Power is paid in the card's own domains; a card with one
      // domain shows that pip, and a multi-domain card the generic one.
      const token = def.domains.length === 1 ? POWER_SYMBOL[def.domains[0]!] : 'A';
      const pips = Array.from({ length: def.cost.power }, () => this.symbol(token) ?? '').join('');
      stats.push(`<span class="stat">${pips}</span>`);
    }
    if (def.might !== null) {
      stats.push(`<span class="stat might">${def.might}${this.symbol('M') ?? ''}</span>`);
    }

    // A card whose text the engine has not modelled plays as a vanilla body;
    // saying so is better than letting the player expect an effect that will
    // never happen.
    const script = this.session.rules.scripts[def.id];
    const unmodelled = script && !script.complete && def.text
      ? '<div class="warn">Rules text not yet modelled — plays as printed stats and keywords.</div>'
      : '';

    const tags = def.tags?.length ? ` · ${def.tags.map(escapeHtml).join(' · ')}` : '';

    // 121. Where the printed card puts them, using the frame's own plates.
    //
    // On a phone the panel shows a *crop* of the artwork, which cuts off the
    // two corners the frame prints the cost and the Might in -- so they are
    // put back, on the actual corner ornaments the cards are printed with
    // (`assets/ui/cost.png` and the three right-hand banners) rather than on
    // chips invented here. The rows underneath that said the same thing are
    // hidden there. None of it is drawn on a desktop: the whole card is on
    // screen with its own frame, and a second cost orb over the printed one is
    // simply two of them.
    //
    // Every position is a CSS custom property, because the plates are
    // artwork and the right offsets are the ones that look right on them.

    // The cost orb, top left. The numeral is the Energy cost, which is what
    // the printed orb carries; 0 is a number the orb shows rather than a
    // reason to hide it.
    const hasCost = def.cost.energy > 0 || def.cost.power > 0;
    const orb = hasCost
      ? `<span class="art-cost"><b>${def.cost.energy}</b></span>` : '';

    // A legend has no cost and prints something else in that corner: two rune
    // sockets naming the domains its deck may be built from (103). They are
    // filled with the domain's own rune over a wash of its colour, and a
    // single-domain legend leaves the second socket empty rather than
    // repeating itself -- an empty socket is what the printed card shows.
    const sockets = def.type === 'legend'
      ? `<span class="art-legend">${def.domains.slice(0, 2).map((d, i) => {
        const rune = domainRune(d);
        return rune
          ? `<span class="art-rune sym-${rune.kind}" style="--slot:${i}">`
            + `<i class="sym">${rune.glyph}</i></span>`
          : '';
      }).join('')}</span>`
      : '';

    // 135.2.e. Power is paid in the card's own domains, and it stacks
    // *downwards* under the orb -- one rune per point, the way the frame
    // prints them.
    const powerToken = def.domains.length === 1 ? POWER_SYMBOL[def.domains[0]!] : 'A';
    // Each pip is a disc of the domain's own colour with a white rune on it,
    // which needs two elements rather than one: the wrapper carries the colour
    // so `background-color: currentColor` can pick it up, and the glyph inside
    // overrides `color` to white. One element cannot do both -- setting the
    // text white would take `currentColor` with it and the disc would go white
    // too, leaving a white rune on a white disc.
    const pip = symbolParts(powerToken);
    const power = def.cost.power > 0 && pip
      ? `<span class="art-power">${
        Array.from({ length: def.cost.power }, () =>
          `<span class="art-pip sym-${pip.kind}"><i class="sym">${pip.glyph}</i></span>`).join('')
      }</span>`
      : '';

    // The right-hand banner. A unit carries its Might on it; a spell and a
    // gear carry their own mark and no number, which is what those plates are
    // drawn for.
    const right = def.might !== null
      ? `<span class="art-right art-might"><b>${def.might}</b></span>`
      : def.type === 'spell' || def.type === 'signatureSpell'
        ? '<span class="art-right art-spell"></span>'
        : def.type === 'gear' ? '<span class="art-right art-gear"></span>' : '';

    // Each word is its own element, so a champion's bar and a gear's can be
    // styled apart without the CSS having to pick one of them out of a
    // sentence. The slug is for the selector; the text stays as printed.
    const slug = (text: string): string =>
      text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const chips = [
      `<span class="art-chip art-chip-type" data-type="${slug(def.type)}">${
        escapeHtml(def.type)}</span>`,
      ...(def.tags ?? []).map((t) =>
        `<span class="art-chip art-chip-tag" data-tag="${slug(t)}">${escapeHtml(t)}</span>`),
    ].join('');

    const overlay = `${orb}${power}${right}${sockets}
      <span class="art-foot">${typeHtml(def.type)}<span class="art-type">${
  chips}</span></span>`;

    return `
      ${this.detailArt(def, overlay)}
      <div class="detail-body">
        <div class="detail-head${def.type === 'legend' ? ' head-legend' : ''}"
             style="--head-bg:${headBackground(def.domains)};${headTexture(def.domains)}">
          <span class="detail-name">${escapeHtml(def.name)}</span>
        </div>
        <div class="detail-type">${escapeHtml(def.type)}${tags}</div>
        <div class="detail-stats">${stats.join('')}</div>
        ${def.text ? `<p class="detail-text">${this.renderText(def.text)}</p>` : ''}
        ${unmodelled}
      </div>`;
  }

  /**
   * Card text with its symbols drawn as symbols.
   *
   * The pipeline stores them as the rules shorthand -- [M], [R], [3] -- because
   * that is what the grammar parses. Here they become glyphs from Riftbound's
   * own icon font, which is what the printed card shows.
   *
   * Reminder clauses in parentheses repeat what a keyword already says, so they
   * are set as an aside rather than as more rules.
   */
  private renderText(text: string): string {
    return this.lines(text).map((line) => this.renderLine(line)).join('\n');
  }

  /** The lines a printed card breaks its text into (see `cardtext.ts`). */
  private lines(text: string): string[] {
    return cardTextLines(text, (word) => KEYWORD_COLOR[word] !== undefined);
  }

  private renderLine(text: string): string {
    // 801. Keywords first: they are bracketed like symbols but are words, and
    // the single-token pass below would leave them as literal "[Ganking]".
    // A value rides with the keyword ("[Assault 2]") and stays inside the chip.
    // The arrow that follows a keyword belongs *to* it -- riftcardmaker draws
    // it as a tail on the chip -- so it is swallowed here rather than left
    // behind as a literal "[>]" sitting after the pill.
    const withKeywords = escapeHtml(text).replace(
      /\[([A-Za-z][A-Za-z-]+)(\s+(?:\d+|X))?\](\[&gt;{1,2}\])?/g,
      (whole, word: string, value: string | undefined, arrow: string | undefined) =>
        this.keywordChip(word, value, !!arrow) ?? whole,
    );
    const withSymbols = withKeywords.replace(
      /\[([A-Za-z]|\d+)\]/g,
      (whole, token: string) => this.symbol(token) ?? whole,
    );
    return withSymbols.replace(/\(([^)]*)\)/g, '<em>($1)</em>');
  }

  /**
   * 801. One keyword as the coloured chip the printed cards use, or null.
   *
   * The standalone keyword *row* above the rules text is gone -- it repeated
   * what the text already says -- but the chips inside the text are not
   * decoration: the colour is riftcardmaker's own grouping, teal for the ones
   * about *when* a card may be played, lime for standing and triggered
   * abilities, grey for the ones naming an action another card takes. Setting
   * them as plain words throws that away.
   */
  private keywordChip(word: string, value?: string, arrow = false): string | null {
    const color = KEYWORD_COLOR[word.toLowerCase()];
    if (!color) return null;
    const label = escapeHtml(word.toUpperCase() + (value ? ` ${value.trim()}` : ''));
    const cls = arrow ? 'kwchip arrow' : 'kwchip';
    return `<b class="${cls}" style="background:${color}">${label}</b>`;
  }

  /** One rules symbol as an icon, or null if it is not one. */
  private symbol(token: string): string | null {
    if (/^\d+$/.test(token)) {
      // Energy is a run of numerals, one glyph each, so [12] reads as 1 then 2
      // -- and each digit is its own element, because the glyph is a disc and
      // two of them in one box cannot be spaced or backed as two discs.
      return [...token].map((d) => `<i class="sym sym-energy">${d}</i>`).join('');
    }
    const found = SYMBOLS[token];
    if (!found) return null;
    // Every symbol has a glyph, and the type says so: a new one added without
    // one will not compile, rather than rendering an empty box that looks like
    // a styling bug. The CSS-drawn substitute that used to stand in for the
    // rainbow rune is gone -- the font had it all along.
    return `<i class="sym sym-${found.kind}">${found.glyph}</i>`;
  }

  /**
   * One player's seat: their face, their score and their name.
   *
   * Deliberately only those three. The zone counts that used to sit here --
   * hand, deck, runes, trash -- are on the table now, beside the piles they
   * describe, which is where a player looks for them anyway. A rail of numbers
   * away from the thing they count is a second place to read the board, and
   * the two can disagree.
   */
  private renderPlate(state: GameState, player: PlayerId, mine: boolean): string {
    // 471. The effective target, which a battlefield can raise. Printing the
    // base score left a player sitting at "8 / 8" while the game carried on.
    const goal = victoryTarget(state, this.session.rules);
    const points = state.players[player].points;

    const legendId = state.zones.legendZone[player][0];
    const legend = legendId !== undefined
      ? this.session.rules.index.get(state.objects[legendId]!.card)
      : null;

    // Inside a Discord Activity the players have names and faces, and using
    // them is the whole point of being in Discord. The legend's art is the
    // fallback portrait everywhere else -- and still the fallback here, for a
    // Discord user who has no avatar set.
    const seat = this.seats[mine ? 'me' : 'them'];
    const portrait = seat?.avatarUrl
      ? `<img class="seat-face" src="${escapeHtml(seat.avatarUrl)}" alt="">`
      : legend
        ? this.artTag(legend.id, 'seat-face')
        : '<div class="seat-face"></div>';
    const who = seat?.name ?? (mine ? 'You' : 'Opponent');

    return `
      <div class="seat-plate">
        <i class="seat-bar"></i>
        <div class="seat-score">
          <span class="seat-points">${points}</span><span class="seat-goal">/ ${goal}</span>
        </div>
        <div class="seat-name">${escapeHtml(who)}</div>
      </div>
      <div class="seat-avatar"><span class="seat-crop">${portrait}</span></div>`;
  }

  /** Takes back the last move, and says so either way. */
  private onRewind(): void {
    if (this.session.rewind()) {
      this.setPrompt('Rewound to before your last move.');
    } else {
      this.setPrompt('Nothing to take back.');
    }
  }

  /**
   * Wires the sound cues.
   *
   * Delegated listeners on the HUD root rather than one per button: the button
   * cluster is rebuilt as the match goes on, and per-node listeners would have
   * to be torn down with it.
   */
  private bindSounds(): void {
    const on = (node: HTMLElement, event: string, fn: (e: Event) => void) => {
      node.addEventListener(event, fn, true);
      this.detach.push(() => node.removeEventListener(event, fn, true));
    };
    on(this.root, 'pointerover', (e) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('button')) audio.play('buttonHover');
    });
    on(this.root, 'click', (e) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('button')) audio.play('buttonClick');
    });
  }

  /**
   * Turns what just happened into a noise.
   *
   * Driven by the engine's own events rather than by the click that caused
   * them, so a card the *opponent* plays sounds the same as one you play, and
   * an effect that plays a card sounds like a card being played.
   */
  onEvents(events: readonly GameEvent[]): void {
    for (const event of events) {
      switch (event.t) {
        case 'draw': audio.play('cardDraw'); break;
        case 'play':
          audio.play(this.session.rules.index.get(event.card).type === 'spell'
            ? 'spell' : 'cardPlay');
          break;
        case 'showdownBegin': audio.play('showdown'); break;
        case 'phase':
          if (event.phase === 'beginning') audio.play('roundStart');
          break;
        default: break;
      }
    }
  }

  /** Names a seat. Redraws, because the plates carry the name. */
  setSeat(seat: 'me' | 'them', identity: SeatIdentity | null): void {
    if (identity) this.seats[seat] = identity;
    else delete this.seats[seat];
    this.update();
  }

  /** Redraws everything derived from game state. */
  update(): void {
    const state = this.session.state;
    const me = this.session.human;
    const them: PlayerId = me === 0 ? 1 : 0;

    const yours = state.turnPlayer === me;
    this.turn.classList.toggle('yours', yours);
    this.turn.innerHTML = `
      <span class="turn-no">Turn ${state.turn}</span>
      <span class="turn-phase">${PHASE_LABEL[state.phase] ?? state.phase}</span>
      <span class="turn-who">${escapeHtml(
        yours ? (this.seats.me ? `${this.seats.me.name}'s turn` : 'Your turn')
              : (this.seats.them?.name ?? 'Opponent'))}</span>`;

    this.plateMe.innerHTML = this.renderPlate(state, me, true);
    this.plateThem.innerHTML = this.renderPlate(state, them, false);

    this.pool.innerHTML = this.renderPool(state, me);

    const canAct = this.session.waitingOnHuman;
    // Labelled from the action the button will actually take, so it can never
    // promise something the engine will refuse.
    const primary = canAct ? this.board.primaryAction() : null;
    this.endTurn.disabled = !canAct || !primary;
    this.endTurn.textContent =
      state.waiting.kind === 'mulligan'
        ? (this.board.mulliganCount > 0 ? `Replace ${this.board.mulliganCount}` : 'Keep hand')
        : primary?.t === 'assignDamage' ? 'Assign damage'
          : primary?.t === 'pass' ? 'Pass'
            : 'End turn';

    // Nothing on a recording is waiting for the watcher, so the button that
    // ends a turn has no turn to end. Disabled it would still read as a thing
    // that might become available; hidden, the transport bar is the only
    // control on screen, which is the truth.
    this.endTurn.hidden = !!this.session.watching;
    el('hud-rewind').hidden = !this.session.canRewind;
    this.renderChoice(state);
    if (!this.logPanel.hidden) this.renderLog();

    // A recording ends where it ended. The card that says so also offers to
    // play again, which is not what somebody watching a saved match wants --
    // and it covers the final board, which is the one they scrubbed to see.
    if (state.waiting.kind === 'gameOver' && !this.session.watching) {
      this.over.hidden = false;
      const won = state.winner === me;
      this.overText.textContent = won
        ? `You win, ${state.players[me].points}–${state.players[them].points}.`
        : `You lose, ${state.players[me].points}–${state.players[them].points}.`;
    } else {
      this.over.hidden = true;
    }
  }


  /**
   * Scrolls the card panel from a wheel turned over the board.
   *
   * The whole point of the panel is to read a card you are pointing at, and
   * the pointer is on the far side of the screen from the text. Moving the
   * mouse over to scroll changes what is being hovered, which changes what the
   * panel is showing -- so the text scrolls out from under the reader on the
   * way to the scrollbar.
   *
   * Returns whether it took the scroll: a panel with nothing to scroll should
   * not swallow the wheel and leave the page unable to move at all.
   */
  scrollDetail(deltaY: number): boolean {
    // The body is rebuilt every time the panel changes card, so it is looked
    // up rather than held.
    const body = this.detail.querySelector<HTMLElement>('.detail-body');
    if (!body || this.detail.hidden) return false;
    const room = body.scrollHeight - body.clientHeight;
    if (room <= 1) return false;

    // Aimed from where it is *going*, not where it is, so two quick notches
    // travel twice as far instead of the second one restarting from wherever
    // the easing had got to.
    const from = this.detailScroll.raf ? this.detailScroll.target : body.scrollTop;
    const target = Math.max(0, Math.min(room, from + deltaY * DETAIL_SCROLL_STEP));
    if (target === from) return false;   // already at that end

    this.detailScroll.target = target;
    this.easeDetail(body);
    return true;
  }

  /**
   * Eases the panel toward where the wheel pointed.
   *
   * Damped rather than jumped, and rather than `behavior: 'smooth'`: a native
   * smooth scroll restarts its animation on every wheel event, so a spin of
   * the wheel reads as stuttering. This is the same shape as the board's own
   * motion -- move a fraction of the remaining distance each frame -- so it
   * arrives quickly and stops without overshooting.
   */
  private easeDetail(body: HTMLElement): void {
    if (this.detailScroll.raf) return;
    const step = (): void => {
      const gap = this.detailScroll.target - body.scrollTop;
      if (Math.abs(gap) < 0.5 || !body.isConnected) {
        if (body.isConnected) body.scrollTop = this.detailScroll.target;
        this.detailScroll.raf = 0;
        return;
      }
      body.scrollTop += gap * 0.2;
      this.detailScroll.raf = requestAnimationFrame(step);
    };
    this.detailScroll.raf = requestAnimationFrame(step);
  }

  /** Stops any easing and forgets where it was going. */
  private resetDetailScroll(): void {
    if (this.detailScroll.raf) cancelAnimationFrame(this.detailScroll.raf);
    this.detailScroll = { target: 0, raf: 0 };
  }

  /**
   * 355.3 / 424. The overlay for a question an effect stopped to ask.
   *
   * Shown only for the human's own choices: the opponent's are answered by the
   * AI without the board ever coming to rest, so the overlay would flicker up
   * and away again with nothing to click.
   */
  private renderChoice(state: GameState): void {
    const ask = state.pendingChoice;
    if (!ask || ask.player !== this.session.human || !this.session.waitingOnHuman) {
      // The board's own gesture menu shares this panel. It can only be open
      // when the engine is not asking, so the two never compete for it.
      if (this.renderMenu()) return;
      this.choice.hidden = true;
      // Emptied rather than left standing: a stale list is a list of cards
      // that can no longer be picked.
      if (this.choiceList.childElementCount > 0) this.choiceList.replaceChildren();
      return;
    }

    const from = state.objects[ask.source];
    const name = from ? this.session.rules.index.get(from.card).name : null;
    this.choiceText.innerHTML = name
      ? `${escapeHtml(ask.prompt)} <b>${escapeHtml(name)}</b>`
      : escapeHtml(ask.prompt);

    const html: string[] = [];
    if (ask.modes) {
      for (const i of ask.modes) {
        const label = ask.modeLabels?.[i];
        html.push(`
          <button class="choice mode" type="button" data-pick="${i}">
            <span class="choice-name">${
              label ? this.renderText(modeLabel(label)) : `Mode ${i + 1}`
            }</span>
          </button>`);
      }
    } else if (ask.names) {
      // 133.8. Naming a tag or a spell: every one the game prints is on offer,
      // which is a long list, so it is laid out as words rather than cards and
      // the container scrolls.
      ask.names.forEach((word, i) => {
        html.push(`
          <button class="choice word" type="button" data-pick="${i}">
            <span class="choice-name">${escapeHtml(word)}</span>
          </button>`);
      });
    } else {
      for (const id of ask.cards ?? []) {
        const object = state.objects[id];
        if (!object) continue;
        const def = this.session.rules.index.get(object.card);
        const cost = def.cost.power > 0
          ? `${def.cost.energy} energy, ${def.cost.power} power`
          : `${def.cost.energy} energy`;
        // What the card *does*, not just what it is called. Being asked to
        // choose between four names tells you nothing about which to pick, and
        // the card art is too small to read the rules box off.
        html.push(`
          <button class="choice" type="button" data-pick="${id}">
            ${this.artTag(def.id, 'choice-blank')}
            <span class="choice-name">${escapeHtml(def.name)}</span>
            <span class="choice-sub">${escapeHtml(def.type)} &middot; ${cost}</span>
            ${def.text ? `<span class="choice-text">${this.renderText(def.text)}</span>` : ''}
          </button>`);
      }
      // 436.1.a. "Recycles any number of them" includes none. The cards are
      // all on offer at once -- that is the point, the decision is taken with
      // everything in view -- so this is how the player says they are done.
      if (ask.decline !== undefined) {
        html.push(`
          <button class="choice word decline" type="button" data-pick="${DECLINE}">
            <span class="choice-name">${escapeHtml(ask.decline)}</span>
          </button>`);
      }
    }
    this.choiceList.innerHTML = html.join('');
    this.choice.hidden = false;
  }

  /**
   * Draws the board's gesture menu, and says whether there was one.
   *
   * 818. A card that can do two things -- an Equipment that can be attached, a
   * unit that can move or use its ability -- has to be asked which, and this is
   * the same panel an engine question uses so there is one place on screen
   * where the game asks the player something.
   */
  private renderMenu(): boolean {
    const menu = this.board.menu;
    if (!menu) return false;
    this.choiceText.innerHTML = `Use <b>${escapeHtml(menu.title)}</b>`;
    this.choiceList.innerHTML = menu.items.map((item, i) => `
      <button class="choice word" type="button" data-menu="${i}">
        <span class="choice-name">${escapeHtml(item.label)}</span>
        ${item.sub ? `<span class="choice-sub">${escapeHtml(item.sub)}</span>` : ''}
      </button>`).join('');
    this.choice.hidden = false;
    return true;
  }

  // -- the match log ---------------------------------------------------------

  private showLog(open: boolean): void {
    this.logPanel.hidden = !open;
    if (open) this.settingsPanel.hidden = true;
    this.settings.showLog = open;
    saveSettings(this.settings);
    if (open) this.renderLog();
  }

  /**
   * Appends whatever is new.
   *
   * Rebuilt-from-scratch would throw away the reader's scroll position every
   * time the opponent moved, which for a log is the whole of its usefulness.
   */
  private renderLog(): void {
    const entries = this.session.log.entries;
    if (entries.length < this.logDrawn) {
      // A new match, or the log rolled over its cap.
      this.logList.replaceChildren();
      this.logDrawn = 0;
    }
    if (this.logDrawn === 0 && entries.length === 0) {
      this.logList.innerHTML = '<div class="log-empty">Nothing yet. Every action either side takes shows up here.</div>';
      return;
    }
    if (this.logDrawn === 0) this.logList.replaceChildren();

    // Only follow the tail when the reader is already at it.
    const atBottom = this.logList.scrollTop + this.logList.clientHeight
      >= this.logList.scrollHeight - 24;

    const html: string[] = [];
    for (const entry of entries.slice(this.logDrawn)) {
      const effects = this.settings.logEffects && entry.effects.length > 0
        ? `<div class="log-effects">${
          entry.effects.map((e) => `<div>${escapeHtml(e)}</div>`).join('')}</div>`
        : '';
      html.push(`
        <div class="log-entry ${entry.side}">
          <div class="log-when">${entry.n} &middot; T${entry.turn} ${escapeHtml(entry.phase)} &middot; ${
        entry.side === 'you' ? 'you' : 'opponent'}</div>
          <div class="log-what">${escapeHtml(entry.action)}</div>
          ${entry.why ? `<div class="log-why">${escapeHtml(entry.why)}</div>` : ''}
          ${effects}
        </div>`);
    }
    this.logList.insertAdjacentHTML('beforeend', html.join(''));
    this.logDrawn = entries.length;
    if (atBottom) this.logList.scrollTop = this.logList.scrollHeight;
  }

  /**
   * Writes the match out as a replay.
   *
   * Just the seed, the difficulty and the actions: the engine is deterministic,
   * so that is enough to rebuild the decks, the shuffle and every draw. Saving
   * board states instead would be larger *and* able to contradict the engine
   * that produced it.
   */
  private saveReplay(): void {
    const replay = this.session.replay();
    const stamp = replay.recorded.replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([JSON.stringify(replay, null, 1)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `riftbound-${replay.seed}-${stamp}.json`;
    link.click();
    // Revoked on the next tick: the click has taken its copy by then, and
    // holding the blob keeps the whole match in memory.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  // -- settings --------------------------------------------------------------

  private showSettings(open: boolean): void {
    this.settingsPanel.hidden = !open;
    // The two share a corner, so only one is up at a time -- but the log going
    // away to make room is not the player closing it, and closing settings puts
    // it back.
    if (open) this.logPanel.hidden = true;
    else if (this.settings.showLog) { this.logPanel.hidden = false; this.renderLog(); }
  }

  private bindSettings(): void {
    const music = el<HTMLInputElement>('set-music');
    const sfx = el<HTMLInputElement>('set-sfx');
    const muted = el<HTMLInputElement>('set-muted');
    const delay = el<HTMLInputElement>('set-delay');
    const motion = el<HTMLInputElement>('set-motion');
    const manualRunes = el<HTMLInputElement>('set-manualrunes');
    const logEffects = el<HTMLInputElement>('set-logeffects');

    music.value = String(Math.round(this.settings.musicVolume * 100));
    sfx.value = String(Math.round(this.settings.sfxVolume * 100));
    muted.checked = this.settings.muted;
    delay.value = String(this.settings.aiDelayMs);
    motion.checked = this.settings.cardMotion;
    manualRunes.checked = this.settings.manualRunes;
    logEffects.checked = this.settings.logEffects;

    const on = (node: HTMLElement, event: string, fn: () => void) => {
      node.addEventListener(event, fn);
      this.detach.push(() => node.removeEventListener(event, fn));
    };
    const commit = () => {
      this.settings.musicVolume = Number(music.value) / 100;
      this.settings.sfxVolume = Number(sfx.value) / 100;
      this.settings.muted = muted.checked;
      this.settings.aiDelayMs = Number(delay.value);
      this.settings.cardMotion = motion.checked;
      this.settings.manualRunes = manualRunes.checked;
      this.settings.logEffects = logEffects.checked;
      saveSettings(this.settings);
      this.applySettings();
    };
    for (const node of [music, sfx, muted, delay, motion, manualRunes, logEffects]) {
      on(node, 'input', commit);
    }
  }

  /** Pushes the settings into the parts of the client that read them. */
  private applySettings(): void {
    el<HTMLOutputElement>('set-music-out').value = `${Math.round(this.settings.musicVolume * 100)}%`;
    el<HTMLOutputElement>('set-sfx-out').value = `${Math.round(this.settings.sfxVolume * 100)}%`;
    el<HTMLOutputElement>('set-delay-out').value = this.settings.aiDelayMs === 0
      ? 'instant' : `${this.settings.aiDelayMs} ms`;
    this.session.aiDelayMs = this.settings.aiDelayMs;
    audio.setVolume(this.settings.sfxVolume, this.settings.muted);
    this.board.cardMotion = this.settings.cardMotion;
    this.board.manualRunes = this.settings.manualRunes;
    // The effects lines are per-entry, so changing that setting has to redraw.
    this.logDrawn = 0;
    this.logList.replaceChildren();
    if (!this.logPanel.hidden) this.renderLog();
  }

  private renderPool(state: GameState, player: PlayerId): string {
    const pool = state.players[player].pool;
    const parts: string[] = [];
    if (pool.energy > 0) parts.push(`<span class="res energy">${pool.energy}</span>`);
    for (const d of DOMAINS) {
      const n = pool.power[d];
      if (n > 0) {
        parts.push(`<span class="res" style="--c:${DOMAIN_HEX[d]}">${n}</span>`);
      }
    }
    if (pool.any > 0) parts.push(`<span class="res any">${pool.any}</span>`);
    // An empty pool is the normal state for most of a turn, and saying so in
    // the middle of the screen is noise rather than information.
    return parts.join('');
  }

  destroy(): void {
    for (const node of this.badges.values()) node.remove();
    this.badges.clear();
    for (const node of this.counts.values()) node.remove();
    this.counts.clear();
    for (const off of this.detach) off();
    this.detach.length = 0;
    this.root.hidden = true;
  }
}
